import { observeKeepPlaying, trackKeepPlaying, trackVerifiedKeepPlaying } from './music-library-jukebox-analytics.js';

(function (window, document) {
    'use strict';
    if (window.iksonJukebox) return;

    var root = null;
    var deadline = 0;
    var generation = 0;
    var pendingNext = null;
    var preparedNext = null;
    var preparedFor = '';
    var playWhenReady = false;
    var fadeSeconds = 1.2;
    var statusPending = false;
    var recent = [];
    var returnSession = new URL(window.location.href).searchParams.get('jukebox_checkout');
    var returnAttempts = 0;
    var everActive = false;
    var playAfterPayment = false;

    function remaining() { return Math.max(0, Math.ceil((deadline - performance.now()) / 1000)); }
    function onMusic() { return window.location.pathname.replace(/\/$/, '') === '/music' && root && root.isConnected; }
    function message(text) {
        if (root && root.isConnected) root.querySelector('[data-jukebox-message]').textContent = text;
    }
    function invalidateNext() {
        generation += 1;
        if (pendingNext) pendingNext.abort();
        pendingNext = null;
        preparedNext = null;
        preparedFor = '';
        playWhenReady = false;
    }
    function render() {
        if (!root || !root.isConnected) return;
        var seconds = remaining();
        var active = seconds > 0;
        if (window.iksonPlayer) window.iksonPlayer.setAutoplayFade(active ? fadeSeconds : 0);
        var time = Math.floor(seconds / 60) + ':' + String(seconds % 60).padStart(2, '0');
        root.classList.toggle('is-active', active);
        var indicator = root.querySelector('[data-jukebox-remaining]');
        indicator.hidden = !active;
        indicator.textContent = time;
        root.querySelector('[data-jukebox-open]').setAttribute('aria-label', active
            ? 'Keep Playing: ' + time + ' remaining' : 'Keep Playing: 60 minutes of continuous music for $1');
        root.querySelector('[data-jukebox-play]').hidden = !active;
        root.querySelector('[data-jukebox-buy]').hidden = !active || root.dataset.jukeboxPurchasing === 'true';
        if (everActive && !active) {
            everActive = false;
            invalidateNext();
            message('Your listening time has ended. You can keep choosing tracks normally.');
        }
    }
    function request(url, options) {
        options = options || {};
        options.credentials = 'same-origin';
        options.cache = 'no-store';
        options.headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
        return fetch(url, options).then(function (response) {
            if (!response.ok) {
                var error = new Error('Request failed');
                error.status = response.status;
                throw error;
            }
            return response.json();
        });
    }
    function clearReturn() {
        var url = new URL(window.location.href);
        url.searchParams.delete('jukebox_checkout');
        url.searchParams.delete('jukebox_cancelled');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        returnSession = null;
    }
    function syncStatus() {
        if (!onMusic() || statusPending) return;
        var target = root;
        var url = new URL(root.dataset.statusUrl, window.location.origin);
        if (returnSession) url.searchParams.set('checkout_session_id', returnSession);
        statusPending = true;
        request(url.href).then(function (state) {
            if (!target.isConnected || target !== root) return;
            deadline = state.active ? performance.now() + Math.max(0, state.expires_at - state.server_now) * 1000 : 0;
            everActive = everActive || state.active;
            trackVerifiedKeepPlaying(state);
            if (returnSession) {
                if (state.checkout_confirmed) {
                    if (window.iksonJukeboxPayment) window.iksonJukeboxPayment.dispose();
                    message('Payment confirmed. Keep Playing is ready.');
                    clearReturn();
                    if (playAfterPayment && (!window.iksonPlayer || !window.iksonPlayer.getState().isPlaying)) nextTrack();
                    if (playAfterPayment) root.querySelector('dialog').close();
                    playAfterPayment = false;
                } else if (++returnAttempts < 10) {
                    message('Checking payment…');
                    window.setTimeout(syncStatus, 2000);
                } else {
                    message('Payment is not confirmed yet. Reopen Keep Playing to check again.');
                }
            }
            render();
            if (!state.active && !returnSession && root.querySelector('dialog').open && window.iksonJukeboxPayment) {
                window.iksonJukeboxPayment.open(root);
            }
        }).catch(function () {
            if (returnSession) message('Unable to confirm payment right now. Reopen Keep Playing to try again.');
        }).finally(function () { statusPending = false; });
    }
    function startPreparedTrack() {
        if (!playWhenReady || !preparedNext || !onMusic() || remaining() <= 0) return;
        var track = preparedNext;
        preparedNext = null;
        playWhenReady = false;
        window.iksonPlayer.activateTrack(track, 0, true);
        message('Keep Playing is on. You can choose another track at any time.');
    }
    function nextTrack() { prepareNext(true); }
    function prepareNext(start) {
        if (!onMusic() || remaining() <= 0 || !window.iksonPlayer) return;
        playWhenReady = playWhenReady || start;
        if (preparedNext) { startPreparedTrack(); return; }
        if (pendingNext) return;
        var player = window.iksonPlayer;
        var current = player.getState().trackId;
        if (!start && preparedFor === current) return;
        preparedFor = current;
        var version = generation;
        var target = root;
        var controller = new AbortController();
        pendingNext = controller;
        var url = new URL(root.dataset.nextUrl, window.location.origin);
        if (current) url.searchParams.set('current', current);
        recent.slice(-8).forEach(function (id) { url.searchParams.append('recent[]', id); });
        request(url.href, { signal: controller.signal }).then(function (data) {
            if (generation !== version || !onMusic() || root !== target || remaining() <= 0) return;
            var fragment = document.createElement('div');
            fragment.innerHTML = data.html;
            var track = fragment.querySelector('[data-trackid][data-track]');
            if (!track || !track.getAttribute('data-track') || track.dataset.mediaUnavailable === 'true') throw new Error('No playable track');
            pendingNext = null;
            preparedNext = track;
            startPreparedTrack();
        }).catch(function (error) {
            if (error.name === 'AbortError' || generation !== version) return;
            if (error.status === 403) { deadline = 0; render(); }
            else if (playWhenReady) message('Shuffle could not continue. Tap Start listening to retry, or choose a track.');
        }).finally(function () { if (pendingNext === controller) pendingNext = null; });
    }
    function init() {
        var candidate = document.querySelector('[data-jukebox]');
        if (candidate === root) return;
        invalidateNext();
        if (window.iksonJukeboxPayment) window.iksonJukeboxPayment.dispose();
        root = candidate;
        observeKeepPlaying(root);
        if (!root) { if (window.iksonPlayer) window.iksonPlayer.setAutoplayFade(0); return; }
        render();
        if (returnSession || new URL(window.location.href).searchParams.has('jukebox_cancelled')) {
            root.querySelector('dialog').showModal();
            if (!returnSession) { message('Payment cancelled. You can still listen and choose tracks normally.'); clearReturn(); }
        }
        syncStatus();
    }
    document.addEventListener('click', function (event) {
        var control = event.target.closest('[data-jukebox-open], [data-jukebox-close], [data-jukebox-buy], [data-jukebox-play]');
        if (!control) return;
        init();
        if (!onMusic()) return;
        var dialog = root.querySelector('dialog');
        if (control.hasAttribute('data-jukebox-open')) {
            dialog.showModal();
            trackKeepPlaying('Opened');
            syncStatus();
        }
        if (control.hasAttribute('data-jukebox-close')) dialog.close();
        if (control.hasAttribute('data-jukebox-play')) {
            if (remaining() <= 0) { render(); return; }
            dialog.close();
            if (!window.iksonPlayer || !window.iksonPlayer.getState().isPlaying) nextTrack();
        }
        if (control.hasAttribute('data-jukebox-buy')) {
            if (window.iksonJukeboxPayment) window.iksonJukeboxPayment.open(root, true);
        }
    });
    document.addEventListener('ikson:player-ended', nextTrack);
    document.addEventListener('ikson:jukebox-payment-confirmed', function (event) {
        returnSession = event.detail.checkoutId;
        returnAttempts = 0;
        playAfterPayment = true;
        syncStatus();
    });
    document.addEventListener('ikson:player-interaction', invalidateNext);
    document.addEventListener('ikson:player-state', function (event) {
        var state = event.detail;
        var id = state.trackId;
        var left = state.duration - state.currentTime;
        // Request only one next choice near the end; no playback, media download
        // or extra player is started by this preparation.
        if (state.isPlaying && left > 0 && left <= 8 && remaining() > left) prepareNext(false);
        if (id && recent[recent.length - 1] !== id) recent = recent.concat(id).slice(-8);
    });
    document.addEventListener('visibilitychange', function () { if (!document.hidden) syncStatus(); });
    window.addEventListener('pageshow', syncStatus);
    window.setInterval(render, 1000);
    window.setInterval(syncStatus, 30000);
    new MutationObserver(init).observe(document.documentElement, { childList: true, subtree: true });
    window.iksonJukebox = { refresh: syncStatus };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})(window, document);
